import React, { useMemo } from 'react';
import {
  formatNumEU,
  formatPct,
  signedNum,
  getTimeframeLabel,
} from '../dummyData';
import { HiddenValue, useValueHide } from '../ValueHideContext';
import { SubscriptionSummary } from '../../../../types/subscription';
import { SummaryInformation } from '../../types';
import { OverviewChartTimeframe, OverviewChartType } from '../../types';
import SummaryChart from '../../Summary/Chart';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectOverviewChartScale,
  selectOverviewChartTimeframe,
  selectOverviewChartType,
  setOverviewChartScale,
  setOverviewChartTimeframe,
  setOverviewChartType,
} from '../../../../store/slices/ui';
import { selectActiveCluster } from '../../../../store/slices/subscription';
import { profitWashColor } from '../../../../colors';
import './index.css';

const TIMEFRAMES = Object.values(OverviewChartTimeframe);

// ── Eye toggle (inline next to label) ──────────────────────────────────────

const EyeToggle: React.FC = () => {
  const { hidden, toggle } = useValueHide();
  return (
    <button
      className="hero-eye-toggle"
      onClick={toggle}
      title={hidden ? 'Show values' : 'Hide values'}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {hidden ? (
          <>
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </>
        ) : (
          <>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </>
        )}
      </svg>
    </button>
  );
};

// ── Hero Component ──────────────────────────────────────────────────────────

interface HeroProps {
  subscriptions: SubscriptionSummary[];
  totalValue: number;
  summaryInformation: SummaryInformation | undefined;
  setSummaryInformation: React.Dispatch<
    React.SetStateAction<SummaryInformation | undefined>
  >;
}

const Hero: React.FC<HeroProps> = ({
  subscriptions,
  totalValue,
  summaryInformation,
  setSummaryInformation,
}) => {
  const dispatch = useDispatch();
  const activeCluster = useSelector(selectActiveCluster);
  const overviewChartTimeframe = useSelector(selectOverviewChartTimeframe);
  const overviewChartType = useSelector(selectOverviewChartType);
  const overviewChartScale = useSelector(selectOverviewChartScale);

  const clusterName = activeCluster === null ? 'Portfolio' : activeCluster;

  const costBasis = useMemo(
    () =>
      subscriptions.reduce(
        (acc, s) => acc + (s.profits?.costBasis.stable || 0),
        0,
      ),
    [subscriptions],
  );

  const totalChangeAbs = totalValue - costBasis;
  const totalChangeRel =
    costBasis !== 0 ? (totalValue - costBasis) / costBasis : 0;
  const profit = totalChangeRel >= 0;

  const dayChangeAbs = summaryInformation?.dayChange ?? 0;
  const dayChangeRel = summaryInformation?.dayChangeRelative ?? 0;
  const dayProfit = dayChangeRel >= 0;

  const napDifference = summaryInformation?.napDifference ?? 0;
  const outperformance = napDifference;
  const outperfPositive = outperformance >= 0;
  // Outperformance % is measured against the non-algorithmic balance, not the
  // total value: (total - nonAlgo) / nonAlgo. The chart already computes this
  // as napDifferenceRelative — use it instead of dividing by totalValue.
  const outperformanceRel = summaryInformation?.napDifferenceRelative ?? 0;

  const currentStableAbs = useMemo(
    () => subscriptions.reduce((acc, s) => acc + s.stableBalance, 0),
    [subscriptions],
  );
  const currentStableRel = totalValue !== 0 ? currentStableAbs / totalValue : 0;

  const amountOfDays = summaryInformation?.amountOfDays || 1;
  const avgDailyAbs = totalChangeAbs / amountOfDays;
  const avgDailyRel = totalChangeRel / amountOfDays;

  const feesPaid = useMemo(
    () =>
      subscriptions.reduce(
        (acc, s) => acc + (s.latestTransaction?.totalFee ?? 0),
        0,
      ),
    [subscriptions],
  );

  const staleSubscriptions = useMemo(
    () => subscriptions.filter((s) => s.priceIsStale),
    [subscriptions],
  );
  const staleExchanges = useMemo(
    () => Array.from(new Set(staleSubscriptions.map((s) => s.exchange))),
    [staleSubscriptions],
  );

  const gradientColor = profitWashColor(profit);

  // Compared against the enum directly, not collapsed to a two-way flag: an
  // `x === FullBalance ? 'full' : 'profit'` ternary lights the Profit Only pill
  // for every mode that is not Full Balance.
  const isChartMode = (chartType: OverviewChartType) =>
    overviewChartType === chartType;

  return (
    <div
      className="hero-container"
      style={{
        background: `linear-gradient(180deg, ${gradientColor} 0%, rgba(0,0,0,0) 70%)`,
      }}
    >
      {/* ── Top row ── */}
      <div className="hero-top">
        {/* Left: label + value + change + 24h */}
        <div className="hero-title-section">
          <div className="hero-label-row">
            <p className="hero-label">{clusterName}</p>
            <EyeToggle />
          </div>

          <h1 className="hero-value">
            <HiddenValue width={180}>${formatNumEU(totalValue)}</HiddenValue>
            {staleSubscriptions.length > 0 && (
              <span
                className="hero-stale-icon"
                title={`Outdated prices (${staleExchanges.join(', ')})`}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </span>
            )}
          </h1>

          {/* Profit since */}
          <div className="hero-change">
            <span
              className={`hero-change-amount ${
                profit ? 'color-positive' : 'color-negative'
              }`}
            >
              <HiddenValue width={80}>{signedNum(totalChangeAbs)}</HiddenValue>
            </span>
            <span
              className={`hero-change-pill ${
                profit ? 'bg-positive' : 'bg-negative'
              }`}
            >
              {formatPct(totalChangeRel)}
            </span>
            <span className="hero-change-since">
              {getTimeframeLabel(overviewChartTimeframe)}
            </span>
          </div>

          {/* 24h change */}
          <div className="hero-change hero-change--24h">
            <span
              className={`hero-change-amount ${
                dayProfit ? 'color-positive' : 'color-negative'
              }`}
            >
              <HiddenValue width={60}>{signedNum(dayChangeAbs)}</HiddenValue>
            </span>
            <span
              className={`hero-change-pill ${
                dayProfit ? 'bg-positive' : 'bg-negative'
              }`}
            >
              {formatPct(dayChangeRel)}
            </span>
            <span className="hero-change-since">since yesterday</span>
          </div>
        </div>

        {/* Right: toggles only */}
        <div className="hero-stats-section">
          {/* Timeframe pills */}
          <div className="hero-pill-row">
            {TIMEFRAMES.map((tf) => (
              <button
                key={tf}
                className={`hero-pill${
                  overviewChartTimeframe === tf ? ' hero-pill--active' : ''
                }`}
                onClick={() => dispatch(setOverviewChartTimeframe(tf))}
              >
                {tf}
              </button>
            ))}
          </div>

          {/* Chart mode toggle */}
          <div className="hero-pill-row">
            <button
              className={`hero-pill${
                isChartMode(OverviewChartType.FullBalance)
                  ? ' hero-pill--active'
                  : ''
              }`}
              onClick={() =>
                dispatch(setOverviewChartType(OverviewChartType.FullBalance))
              }
            >
              Full Balance
            </button>
            <button
              className={`hero-pill${
                isChartMode(OverviewChartType.ProfitOnly)
                  ? ' hero-pill--active'
                  : ''
              }`}
              onClick={() =>
                dispatch(setOverviewChartType(OverviewChartType.ProfitOnly))
              }
            >
              Profit Only
            </button>
            <button
              className={`hero-pill${
                isChartMode(OverviewChartType.Outperformance)
                  ? ' hero-pill--active'
                  : ''
              }`}
              onClick={() =>
                dispatch(setOverviewChartType(OverviewChartType.Outperformance))
              }
            >
              Outperformance
            </button>
          </div>

          {/* Y-axis scale toggle */}
          <div className="hero-pill-row">
            <button
              className={`hero-pill${
                overviewChartScale === 'lin' ? ' hero-pill--active' : ''
              }`}
              onClick={() => dispatch(setOverviewChartScale('lin'))}
            >
              Lin
            </button>
            <button
              className={`hero-pill${
                overviewChartScale === 'log' ? ' hero-pill--active' : ''
              }`}
              onClick={() => dispatch(setOverviewChartScale('log'))}
            >
              Log
            </button>
          </div>
        </div>
      </div>

      {/* ── Chart area ── */}
      <div className="hero-chart-wrapper">
        <div className="hero-chart-inner">
          <SummaryChart
            subscriptions={subscriptions}
            setSummaryInformation={setSummaryInformation}
            totalValue={totalValue}
          />

          {/* Stats overlaid at bottom of chart, above x-axis */}
          <div className="hero-overlaid-stats">
            {/* Outperformance */}
            <div className="hero-stat-col">
              <span className="hero-stat-col-label">Outperformance</span>
              <p
                className={`hero-stat-col-value ${
                  outperfPositive ? 'color-positive' : 'color-negative'
                }`}
              >
                <HiddenValue width={80}>
                  {signedNum(outperformance)}
                </HiddenValue>{' '}
                <span className="hero-stat-col-pct">
                  {summaryInformation ? formatPct(outperformanceRel) : ''}
                </span>
              </p>
            </div>

            {/* Current Stable */}
            <div className="hero-stat-col">
              <span className="hero-stat-col-label">Current Stable</span>
              <p className="hero-stat-col-value color-neutral">
                <HiddenValue width={60}>
                  ${formatNumEU(currentStableAbs)}
                </HiddenValue>{' '}
                <span className="hero-stat-col-pct">
                  {(currentStableRel * 100).toFixed(1)}%
                </span>
              </p>
            </div>

            {/* Avg Daily */}
            <div className="hero-stat-col">
              <span className="hero-stat-col-label">Avg Daily</span>
              <p
                className={`hero-stat-col-value ${
                  avgDailyRel >= 0 ? 'color-positive' : 'color-negative'
                }`}
              >
                <HiddenValue width={60}>{signedNum(avgDailyAbs)}</HiddenValue>{' '}
                <span className="hero-stat-col-pct">
                  {formatPct(avgDailyRel)}
                </span>
              </p>
            </div>

            {/* Fees Paid */}
            <div className="hero-stat-col">
              <span className="hero-stat-col-label">Fees Paid</span>
              <p className="hero-stat-col-value color-neutral">
                <HiddenValue width={60}>${formatNumEU(feesPaid)}</HiddenValue>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
